import type { InsuranceCustomerType } from '@/general/src/insuranceCustomerType/shared.js'
import type { InsuranceProvider } from '@/general/src/insuranceProvider/shared.js'
import { type InsuranceScenarioKey, insuranceScenariosGenerals } from '@/general/src/insuranceScenario/shared.js'
import { personalOsagoGeneral, zPersonalOsagoInput } from '@/general/src/insuranceType/personalOsago/shared.js'
import { checkEnumEq } from '@/general/src/other/checkEnumEq.js'
import { getConstKeys } from '@/general/src/other/getConstKeys.js'
import type { InsuranceType as InsuranceTypePrisma } from '@/backend/src/services/other/prisma.js'
import { z } from 'zod'

export const insurancePersonalTypesMap = {
  personalOsago: 'ОСАГО физ. лица',
  personalKasko: 'КАСКО физ. лица',
}
export const insurancePersonalTypes = getConstKeys(insurancePersonalTypesMap)
export type InsurancePersonalType = keyof typeof insurancePersonalTypesMap
export const zInsurancePersonalType = z.enum(insurancePersonalTypes)
export const insurancePersonalTypesOptions = insurancePersonalTypes.map((key) => ({
  value: key,
  label: insurancePersonalTypesMap[key],
}))

export const insuranceBusinessTypesMap = {}
export const insuranceBusinessTypes = getConstKeys(insuranceBusinessTypesMap)
export type InsuranceBusinessType = keyof typeof insuranceBusinessTypesMap
export const zInsuranceBusinessType = z.enum(insuranceBusinessTypes)
export const insuranceBusinessTypesOptions = insuranceBusinessTypes.map((key) => ({
  value: key,
  label: insuranceBusinessTypesMap[key],
}))

export const insuranceTypesMap = {
  ...insurancePersonalTypesMap,
  ...insuranceBusinessTypesMap,
}
export const insuranceTypes = getConstKeys(insuranceTypesMap)
export type InsuranceType = keyof typeof insuranceTypesMap
checkEnumEq<InsuranceType, InsuranceTypePrisma, true>()
export const zInsuranceType = z.enum(insuranceTypes)
export const insuranceTypesOptions = insuranceTypes.map((key) => ({
  value: key,
  label: insuranceTypesMap[key],
}))
export const getInsuranceTypeOptions = ({
  insuranceCustomerType,
}: {
  insuranceCustomerType: InsuranceCustomerType
}) => {
  return insuranceCustomerType === 'business' ? insuranceBusinessTypesOptions : insurancePersonalTypesOptions
}

export const insuranceTypesByCustomerTypesMap = {
  business: insuranceBusinessTypesMap,
  personal: insurancePersonalTypesMap,
}

export const getInsuranceTypeTitle = (type: keyof typeof insuranceTypesMap) => {
  return insuranceTypesMap[type]
}

export const isInsuranceTypeSuitableToCustomerType = ({
  insuranceType,
  insuranceCustomerType,
}: {
  insuranceType: InsuranceType
  insuranceCustomerType: InsuranceCustomerType
}) => {
  return insuranceCustomerType === 'business'
    ? !!insuranceBusinessTypesMap[insuranceType as InsuranceBusinessType]
    : !!insurancePersonalTypesMap[insuranceType]
}

export const getInsuranceTypeProviders = ({ insuranceType }: { insuranceType: InsuranceType }): InsuranceProvider[] => {
  return insuranceScenariosGenerals
    .filter((insuranceScenarioGeneral) => insuranceScenarioGeneral.insuranceType === insuranceType)
    .map((insuranceScenarioGeneral) => insuranceScenarioGeneral.insuranceProvider)
}

export const isInsuranceProviderSuitableToInsuranceType = ({
  insuranceType,
  insuranceProvider,
}: {
  insuranceType: InsuranceType
  insuranceProvider: InsuranceProvider
}) => {
  const availableInsuranceProviders = getInsuranceTypeProviders({ insuranceType })
  return availableInsuranceProviders.includes(insuranceProvider)
}

export const getInsuranceTypeInputZSchema = ({ insuranceType }: { insuranceType: InsuranceType }) => {
  return {
    personalOsago: zPersonalOsagoInput,
    // TODO: fix for kasko
    personalKasko: zPersonalOsagoInput,
  }[insuranceType]
}

export type InsuranceTypeGeneral = {
  title: string
  insuranceType: InsuranceType
  insuranceCustomerType: InsuranceCustomerType
  zInput: z.ZodType
  exampleValues: Record<string, any>
  insuranceScenariosKeys: InsuranceScenarioKey[]
}
export const insuranceTypesGenerals = [personalOsagoGeneral]
export const getInsuranceTypeGeneral = ({ insuranceType }: { insuranceType: InsuranceType }) => {
  return insuranceTypesGenerals.find(
    (insuranceTypeGeneral) => insuranceTypeGeneral.insuranceType === insuranceType
  ) as InsuranceTypeGeneral
}
